import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Bitcoin, Menu, X, LayoutDashboard, Link2, Droplets, Wallet, LogOut, User } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = isAuthenticated
    ? [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard-link' },
        { to: '/links', label: 'My Links', icon: Link2, testId: 'nav-links-link' },
        { to: '/faucet', label: 'Faucet', icon: Droplets, testId: 'nav-faucet-link' },
        { to: '/wallet', label: 'Wallet', icon: Wallet, testId: 'nav-wallet-link' },
      ]
    : [];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-landing-link"
            className="flex items-center gap-2 text-xl font-bold font-heading"
          >
            <Bitcoin className="w-8 h-8 text-[#F7931A]" />
            <span className="hidden sm:block text-gradient-gold">SatoshiShort</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.to)
                    ? 'bg-[#F7931A]/20 text-[#F7931A]'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {/* Balance Display */}
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/30">
                  <Bitcoin className="w-4 h-4 text-[#F7931A]" />
                  <span className="font-mono text-sm text-[#F7931A]" data-testid="user-btc-balance">
                    {user?.balance_satoshis?.toLocaleString() || 0} sats
                  </span>
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      data-testid="nav-user-menu-btn"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#F7931A]/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#F7931A]" />
                      </div>
                      <span className="hidden sm:block text-sm font-medium">{user?.username}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#131622] border-white/10">
                    <DropdownMenuItem className="text-slate-300">
                      <User className="w-4 h-4 mr-2" />
                      {user?.email}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      data-testid="nav-logout-btn"
                      className="text-red-400 focus:text-red-400 cursor-pointer"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth">
                  <Button
                    variant="ghost"
                    data-testid="nav-login-button"
                    className="text-slate-300 hover:text-white hover:bg-white/5"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/auth?tab=register">
                  <Button
                    data-testid="nav-register-button"
                    className="btn-gold rounded-lg"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-white/5"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-${link.testId}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.to)
                    ? 'bg-[#F7931A]/20 text-[#F7931A]'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
            {isAuthenticated && (
              <div className="flex items-center gap-2 px-4 py-3 mt-2 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/30">
                <Bitcoin className="w-5 h-5 text-[#F7931A]" />
                <span className="font-mono text-sm text-[#F7931A]">
                  {user?.balance_satoshis?.toLocaleString() || 0} sats
                </span>
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
